type RecorderFunc = {
    start: () => void;
    stop: () => void;
    clear: () => void;
    closeMicrophone: () => void;
}

type PayloadType = {
    index: number;
    result: any;
    stash_result: any;
    time: number;
    begin_time: number;
    words: any[];
}

interface Props {
    wsUrl?: string;
    recordStart?: (payload: PayloadType) => void;
    recordChange?: (payload: PayloadType) => void;
    recordEnd?: (payload: PayloadType) => void;
    recordTrans?: (payload: PayloadType) => void;
    onSend?: (msg: any) => void;
    onError?: (error: any) => void;
}

export class Recorder {
    wsUrl = '';
    WSObject: WebSocket | null = null;
    recorderFunc: RecorderFunc | null = null;
    perm: boolean = true;
    recordStart: ((payload: PayloadType) => void) | undefined | null;
    recordChange: ((payload: PayloadType) => void) | undefined | null;
    recordEnd: ((payload: PayloadType) => void) | undefined | null;
    recordTrans: ((payload: PayloadType) => void) | undefined | null;
    onError: ((error: any) => void) | undefined | null;
    beforeSend: ((msg: any) => void) | undefined | null;
    blobData: any[] = [];
    blobEventData: Blob[] = [];
    mediaRecorder: MediaRecorder | null = null;

    constructor(props: Props) {
        this.wsUrl = props.wsUrl || '';
        if (props.recordStart) this.recordStart = props.recordStart;
        if (props.recordChange) this.recordChange = props.recordChange;
        if (props.recordEnd) this.recordEnd = props.recordEnd;
        if (props.recordTrans) this.recordTrans = props.recordTrans;
        if (props.onError) this.onError = props.onError;
        if (props.onSend) this.beforeSend = props.onSend;
        try {
            //@ts-ignore
            navigator.permissions.query({ name: "microphone" }).then((result) => {
                if (result.state === "granted") {
                    this.perm = true;
                } else if (result.state === "prompt") {
                    this.perm = true;
                } else if (result.state === "denied") {
                    this.perm = false;
                }
                result.addEventListener("change", () => {
                    this.perm = result.state !== "denied";
                });
            }).catch(() => {
                // WebView 不支持 permissions API，跳过
            });
        } catch {
            // WebView 不支持 permissions API，跳过
        }
    }

    async createRecord(stream: MediaStream, wsSend: (stream: any) => void, beforeSend?: (stream: any) => void, blobData?: any[], onError?: (stream: any) => void): Promise<RecorderFunc> {
        //@ts-ignore
        const context = new (window.AudioContext || window.webkitAudioContext)();
        const audioInput = context.createMediaStreamSource(stream);
        //@ts-ignore
        const recorder = context.createScriptProcessor(4096, 1, 1);
        const audioData = {
            size: 0, //录音文件长度
            buffer: [], //录音缓存
            inputSampleRate: 48000, //输入采样率; 一般为本机浏览器默认采样率
            inputSampleBits: 16, //输入采样数位
            outputSampleRate: 16000, //输出采样率
            outputSampleBits: 16, //输出采样位
            clear: function () {
                this.buffer = [];
                this.size = 0;
            },
            input: function (data: string | any[] | Iterable<number>) {
                //@ts-ignore
                this.buffer.push(new Float32Array(data));
                //@ts-ignore
                this.size += data.length;
            },
            compress: function () {
                //对数据 进行 合并压缩
                const data = new Float32Array(this.size);
                let offset = 0;
                for (let i = 0; i < this.buffer.length; i++) {
                    data.set(this.buffer[i], offset);
                    //@ts-ignore
                    offset += this.buffer[i].length;
                }
                let compression = parseInt(String(this.inputSampleRate / this.outputSampleRate));
                let length = data.length / compression;
                let result = new Float32Array(length);
                let index = 0,
                    j = 0;
                while (index < length) {
                    result[index] = data[j];
                    j += compression;
                    index++;
                }
                return result;
            },
            encodePCM: function () {
                let sampleBits = Math.min(this.inputSampleBits, this.outputSampleBits);
                let bytes = this.compress();
                let dataLength = bytes.length * (sampleBits / 8);
                let buffer = new ArrayBuffer(dataLength);
                let data = new DataView(buffer);
                let offset = 0;
                for (let i = 0; i < bytes.length; i++, offset += 2) {
                    let s = Math.max(-1, Math.min(1, bytes[i]));
                    data.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
                }
                return new Blob([data]);
            },
        };

        const sendAudioData = function () {
            //对数据 分片处理
            let reader = new FileReader();
            reader.onload = (e) => {
                let outBuffer = e.target?.result;
                //@ts-ignore
                let arr = new Int8Array(outBuffer);
                if (arr.length > 0) {
                    let tmpArr = new Int8Array(1024);
                    let j = 0;
                    for (let i = 0; i < arr.byteLength; i++) {
                        tmpArr[j++] = arr[i];
                        if ((i + 1) % 1024 === 0) {
                            wsSend(tmpArr);
                            if (arr.byteLength - i - 1 >= 1024) {
                                tmpArr = new Int8Array(1024);
                            } else {
                                tmpArr = new Int8Array(arr.byteLength - i - 1);
                            }
                            j = 0;
                        }
                        if (i + 1 === arr.byteLength && (i + 1) % 1024 !== 0) {
                            wsSend(tmpArr);
                        }
                    }
                }
            };
            reader.onerror = (e) => {
                if (onError) onError(e);
            };
            reader.readAsArrayBuffer(audioData.encodePCM());
            audioData.clear();
        };
        //@ts-ignore
        recorder.onaudioprocess = function (e) {
            const inputBuffer = e.inputBuffer.getChannelData(0);
            audioData.input(inputBuffer);
            if (beforeSend) beforeSend(e.inputBuffer);
            if (blobData) {
                blobData.push(e.inputBuffer);
            }
            sendAudioData();
        };

        this.recorderFunc = {
            //@ts-ignore
            start: function () {
                audioInput.connect(recorder);
                recorder.connect(context.destination);
            },
            //@ts-ignore
            stop: function () {
                recorder.disconnect();
            },
            //@ts-ignore
            clear: function () {
                audioData.clear();
            },
            closeMicrophone: () => {
                context?.close();
                stream.getTracks().forEach(ele => ele.stop());
            }
        };
        return this.recorderFunc;
    }

    wsSend(msg: any) {
        try {
            this.WSObject?.send(msg);
        } catch (e) {
            console.error(e, '=====error====');
            if (this.onError) this.onError(e);
        }
    }

    connectWebSocket(recorder: RecorderFunc) {
        // WSS链接地址是创建实时记录并成功获得推流地址返回的MeetingJoinUrl。
        const ws = new WebSocket(this.wsUrl);
        ws.binaryType = "arraybuffer"; //传输的是 ArrayBuffer 类型的数据
        ws.onopen = function () {
            if (ws.readyState === 1) {
                recorder.start();
            }
            const params = {
                header: {
                    name: "StartTranscription",
                    namespace: "SpeechTranscriber",
                },
                payload: {
                    format: "pcm", // 示例为pcm格式，可按需选择格式。注意如果非pcm格式，则样例中encodePCM方法不能使用，需自行实现数据格式转换。
                },
            };
            ws.send(JSON.stringify(params));
        };

        ws.onmessage = (msg) => {
            console.info(msg);
            // msg.data 是接收到的数据（具体可参考「实时推流返回事件」）
            // 根据业务处理数据
            if (typeof msg.data === "string") {
                const dataJson = JSON.parse(msg.data);
                switch (dataJson.header.name) {
                    case "SentenceBegin": {
                        // 句子开始事件
                        if (this.recordStart) this.recordStart(dataJson.payload);
                        console.log("句子", dataJson.payload.index, "开始");
                        break;
                    }
                    case "TranscriptionResultChanged": {
                        // 句中识别结果变化事件
                        if (this.recordChange) this.recordChange(dataJson.payload);
                        console.log("句子" + dataJson.payload.index + "中间结果:", dataJson.payload.result);
                        break;
                    }
                    case "SentenceEnd": {
                        // 句子结束事件
                        if (this.recordEnd) this.recordEnd(dataJson.payload);
                        console.log("句子" + dataJson.payload.index + "结束:", dataJson.payload.result + dataJson.payload.stash_result.text);
                        break;
                    }
                    case "ResultTranslated": {
                        // 识别结果翻译事件
                        if (this.recordTrans) this.recordTrans(dataJson.payload);
                        console.log("句子翻译结果", JSON.stringify(dataJson.payload.translate_result));
                        break;
                    }
                }
            }
        };
        ws.onerror = (err) => {
            if (this.onError) this.onError(err);
            console.error(err);
        };
        ws.onclose = () => {
            this.WSObject = null;
            recorder.stop();
        };
        this.WSObject = ws;
    }

    get status() {
        return this.perm;
    }

    startRecord() {
        /**
         * 注：以下示例是使用浏览器原生 navigator.getUserMedia方法，基础的实现验证听悟API服务可调通。您可以根据业务场景需要，选用合适的录音库或录音方法，实现更多设备兼容（如浏览器Web&H5、小程序、NativeApp等，不同场景的录音方法不同）
         */
        //@ts-ignore
        navigator.getUserMedia = navigator.getUserMedia || navigator.webkitGetUserMedia;
        //@ts-ignore
        if (!navigator.getUserMedia) {
            console.log("浏览器不支持音频输入");
            // eslint-disable-next-line no-throw-literal
            throw '浏览器不支持音频输入';
        } else {
            //@ts-ignore
            navigator.getUserMedia(
                { audio: true },
                async (stream: MediaStream) => {
                    this.mediaRecorder = new MediaRecorder(stream);
                    this.mediaRecorder.ondataavailable = event => {
                        this.blobEventData.push(event.data);
                    };
                    this.mediaRecorder.start();
                    const recorder = await this.createRecord(stream, this.wsSend.bind(this), this.beforeSend?.bind(this), this.blobData, this.onError?.bind(this));
                    this.connectWebSocket(recorder);
                },
                (error: any) => {
                    if (this.onError) this.onError(error);
                    console.error(error);
                    if (error.name === 'NotAllowedError') {
                        this.perm = false;
                    }
                }
            );
        }
    }

    stopRecord() {
        if (this.WSObject) {
            const params = {
                header: {
                    name: "StopTranscription",
                    namespace: "SpeechTranscriber",
                },
                payload: {},
            };
            this.WSObject?.send(JSON.stringify(params));
            this.mediaRecorder?.stop();
            setTimeout(() => {
                this.WSObject?.close();
            }, 10000);
            this.recorderFunc?.stop();
        }
    }

    closeMicrophone() {
        this.stopRecord();
        this.recorderFunc?.closeMicrophone();
    }
}
